"use client";

import { useClientConfigProvider } from "@/app/config/client-config-provider";
import { useMixPanel } from "@/app/context/mixPanelContext";
import {
  UniversalStorageConstants,
  UniversalStorageUtils,
  useAppContext,
} from "@thriwe-2-0/thriwe-ui-utils";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import AppRoutes from "@/app/constants/routes";
import { IoCloseSharp } from "react-icons/io5";
import AinaIntroModal from "./AinaIntroModal";
import ChatbotSplash from "./ChatbotSplash";

const STORAGE_KEYS = {
  CHATBOT_ENABLED: "aina_chatbot_enabled",
  SUPPORT_ENABLED: "aina_support_enabled",
  INTRO_MODAL: "aina_intro_modal", // stores { isEnabled: boolean, isDismissed: boolean }
} as const;

type IntroModalStorage = {
  isEnabled: boolean;
  isDismissed: boolean;
};

// Storage goes through UniversalStorageUtils, which reads/writes session- or
// local-storage based on the client's configured `nativeDataSavingType`. It
// JSON-serialises on write and parses on read, so objects are stored directly.
function getIntroModalStorage(storageType: any): IntroModalStorage {
  return (
    UniversalStorageUtils.getItem(storageType, STORAGE_KEYS.INTRO_MODAL) ?? {
      isEnabled: false,
      isDismissed: false,
    }
  );
}

function saveIntroModalStorage(
  storageType: any,
  data: Partial<IntroModalStorage>
) {
  const current = getIntroModalStorage(storageType);
  UniversalStorageUtils.setItem(storageType, STORAGE_KEYS.INTRO_MODAL, {
    ...current,
    ...data,
  });
}

export default function FloatingChatBotButton() {
  const { getImagePath, config } = useClientConfigProvider();
  // Session- vs local-storage is decided per client by `nativeDataSavingType`.
  const storageType = config?.nativeDataSavingType;

  const [enableChatbot, setEnableChatbot] = useState<boolean>(
    () =>
      UniversalStorageUtils.getItem(storageType, STORAGE_KEYS.CHATBOT_ENABLED) ===
      true
  );

  const [enableSupport, setEnableSupport] = useState<boolean>(
    () =>
      UniversalStorageUtils.getItem(storageType, STORAGE_KEYS.SUPPORT_ENABLED) ===
      true
  );

  const [showModal, setShowModal] = useState<boolean>(() => {
    const { isEnabled, isDismissed } = getIntroModalStorage(storageType);
    return isEnabled && !isDismissed;
  });

  const [isSupportOpen, setIsSupportOpen] = useState<boolean>(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [chatbotFrameReady, setChatbotFrameReady] = useState<boolean>(false);

  const [supportsHover, setSupportsHover] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(hover: hover)");
    const update = () => setSupportsHover(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  const [chatbotLoaded, setChatbotLoaded] = useState<boolean>(false);
  const [supportLoaded, setSupportLoaded] = useState<boolean>(false);

  const {
    instances: { apiRepository },
  } = useAppContext();
  const { trackEvent } = useMixPanel();
  const pathname = usePathname();

  const env = process.env.NEXT_PUBLIC_ENV;

  const chatbotUrl = useMemo(() => {
    return env === "PRODUCTION"
      ? process.env.NEXT_PUBLIC_CHATBOT_URL_PROD
      : process.env.NEXT_PUBLIC_CHATBOT_URL_STAG;
  }, [env]);

  const supportUrl = useMemo(() => {
    return env === "PRODUCTION"
      ? process.env.NEXT_PUBLIC_SUPPORT_URL_PROD
      : process.env.NEXT_PUBLIC_SUPPORT_URL_STAG;
  }, [env]);

  const authToken = UniversalStorageUtils.getItem(
    storageType,
    UniversalStorageConstants.AUTH_TOKEN
  );
  const chatbotFrameKey = `${authToken ?? "anon"}`;

  const chatbotLink = useMemo(() => {
    if (!chatbotUrl) return "";
    return authToken ? `${chatbotUrl}?token=${authToken}` : chatbotUrl;
  }, [chatbotUrl, authToken]);

  const supportLink = useMemo(() => {
    if (!supportUrl) return "";
    return authToken ? `${supportUrl}?token=${authToken}` : supportUrl;
  }, [supportUrl, authToken]);

  // The auth pages still mount this component, and after a successful login the
  // auth token is set while the user is briefly kept on the login page (a modal
  // handles navigation). Treat those pages as "not logged in" so the chatbot
  // doesn't pop in / fire its feature API until the user actually lands inside
  // the app.
  const isAuthPage =
    pathname === AppRoutes.login ||
    pathname === AppRoutes.signUp ||
    pathname === AppRoutes.forgotPassword;
  const isLoggedIn = !!authToken && !isAuthPage;

  const openChatbot = useCallback(() => {
    if (!chatbotLink) return;
    trackEvent?.("chatbot_opened");
    setChatbotLoaded(true);
    setIsSupportOpen(false);
    setIsChatbotOpen(true);
    setIsMenuOpen(false);
  }, [chatbotLink, trackEvent]);

  const openSupport = useCallback(() => {
    trackEvent?.("support_opened");
    setSupportLoaded(true);
    setIsChatbotOpen(false);
    setIsSupportOpen(true);
    setIsMenuOpen(false);
  }, [trackEvent]);

  // Only called once per session — skipped if CHATBOT_ENABLED is already cached.
  // Held off until the user is past the auth pages so it doesn't fire (and flip
  // the button to its chatbot state) while the post-login modal is still up.
  useEffect(() => {
    if (!authToken || isAuthPage) return;

    const cached = UniversalStorageUtils.getItem(
      storageType,
      STORAGE_KEYS.CHATBOT_ENABLED
    );
    if (cached !== null && cached !== undefined) return;

    apiRepository
      ?.getFeatureConfig({ feature: "chatbot_enabled" })
      .then((res: any) => {
        const isEnabled = res.data?.chatbot_enabled?.enabled ?? false;
        const isModalEnabled = res.data?.pop_enabled ?? false;

        UniversalStorageUtils.setItem(
          storageType,
          STORAGE_KEYS.CHATBOT_ENABLED,
          isEnabled
        );
        saveIntroModalStorage(storageType, {
          isEnabled: isModalEnabled,
          isDismissed: false,
        });

        setEnableChatbot(isEnabled);
        setShowModal(isModalEnabled);
      })
      .catch(() => {
        UniversalStorageUtils.setItem(
          storageType,
          STORAGE_KEYS.CHATBOT_ENABLED,
          false
        );
        setEnableChatbot(false);
      });
  }, [authToken, isAuthPage, apiRepository, storageType]);

  // Only called once per session — skipped if SUPPORT_ENABLED is already cached.
  useEffect(() => {
    if (!authToken) return;

    const cached = UniversalStorageUtils.getItem(
      storageType,
      STORAGE_KEYS.SUPPORT_ENABLED
    );
    if (cached !== null && cached !== undefined) return;

    apiRepository
      ?.getFeatureConfig({ feature: "support_enabled" })
      .then((res: any) => {
        const isEnabled = res.data?.support_enabled?.enabled ?? false;

        UniversalStorageUtils.setItem(
          storageType,
          STORAGE_KEYS.SUPPORT_ENABLED,
          isEnabled
        );
        setEnableSupport(isEnabled);
      })
      .catch(() => {
        UniversalStorageUtils.setItem(
          storageType,
          STORAGE_KEYS.SUPPORT_ENABLED,
          false
        );
        setEnableSupport(false);
      });
  }, [authToken, apiRepository, storageType]);

  // Lock background scroll while the intro modal or a chatbot/support panel is
  // open so scrolling over the overlay doesn't chain through to the page.
  // useEffect(() => {
  //   if (typeof document === "undefined") return;
  //   const isPopupOpen = showModal || isChatbotOpen || isSupportOpen;
  //   const { documentElement: html, body } = document;
  //   html.classList.toggle("aina-scroll-lock", isPopupOpen);
  //   body.classList.toggle("aina-scroll-lock", isPopupOpen);
  //   return () => {
  //     html.classList.remove("aina-scroll-lock");
  //     body.classList.remove("aina-scroll-lock");
  //   };
  // }, [showModal, isChatbotOpen, isSupportOpen]);

  // On logout (token cleared) close any open popup so a stale frame isn't left
  // visible while the iframe remounts via its `key={authToken ?? "anon"}`.
  useEffect(() => {
    if (!authToken) {
      setIsChatbotOpen(false);
      setIsSupportOpen(false);
      setChatbotLoaded(false);
      setSupportLoaded(false);
    }
  }, [authToken]);

  const showChatbot = isLoggedIn && enableChatbot && !!chatbotLink;
  const showSupport = isLoggedIn && enableSupport && !!supportLink;

  const options = useMemo(() => {
    const opts: {
      id: string;
      label: string;
      icon: React.ReactNode;
      action: () => void;
    }[] = [];

    if (showChatbot) {
      opts.push({
        id: "chatbot",
        label: "Start Redeeming",
        icon: (
          <Image
            src={getImagePath("bookmark.svg")}
            alt=""
            width={32}
            height={32}
            className="aina-menu__icon"
          />
        ),
        action: openChatbot,
      });
    }
    if (showSupport) {
      opts.push({
        id: "support",
        label: "Having trouble?",
        icon: (
          <Image
            src={getImagePath("support.svg")}
            alt=""
            width={32}
            height={32}
            className="aina-menu__icon"
          />
        ),
        action: openSupport,
      });
    }
    return opts;
  }, [showChatbot, showSupport, openChatbot, openSupport, getImagePath]);

  const hasMenu = options.length > 1;

  const onButtonClick = useCallback(() => {
    // The button is a non-interactive highlight while the intro modal is open.
    if (showModal) return;
    if (hasMenu) {
      // First click opens the menu; clicking again while it's open launches the
      // chatbot directly.
      if (isMenuOpen) {
        openChatbot();
      } else {
        setIsMenuOpen(true);
      }
    } else {
      options[0]?.action();
    }
  }, [showModal, hasMenu, isMenuOpen, openChatbot, options]);

  // Briefly reveal the menu shortly after every route change so users notice
  // the button. Reset to closed first, then re-open on the next frame so the
  // entry transition replays even when navigating while the menu is still open
  // (otherwise setIsMenuOpen(true) is a no-op and only the exit animates).
  useEffect(() => {
    if (!hasMenu) return;
    setIsMenuOpen(false);
    // Don't reveal (and force-close any open menu) while the intro modal is up.
    if (showModal) return;
    let openRaf: number;
    // Wait 1s after the route change before revealing.
    const openTimeout = setTimeout(() => {
      openRaf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsMenuOpen(true));
      });
    }, 1000);
    const hideTimeout = setTimeout(() => setIsMenuOpen(false), 4000);
    return () => {
      clearTimeout(openTimeout);
      cancelAnimationFrame(openRaf);
      clearTimeout(hideTimeout);
    };
  }, [pathname, hasMenu, showModal]);

  if (options.length === 0) return null;

  return (
    <>
      <div
        className={`aina-wrapper${showModal ? " aina-wrapper--spotlight" : ""}`}
        onMouseEnter={() =>
          !showModal && supportsHover && hasMenu && setIsMenuOpen(true)
        }
        onMouseLeave={() => supportsHover && hasMenu && setIsMenuOpen(false)}
      >
        <AinaIntroModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            saveIntroModalStorage(storageType, { isDismissed: true });
          }}
          onPrimaryAction={() => {
            // Dismiss the intro modal first, otherwise the chatbot popup opens
            // behind it.
            setShowModal(false);
            saveIntroModalStorage(storageType, { isDismissed: true });
            openChatbot();
          }}
        />

        {hasMenu && (
          <div className={`aina-menu ${isMenuOpen ? "is-open" : ""}`}>
            {options.map((option) => (
              <button
                key={option.id}
                className="aina-menu__item"
                onClick={option.action}
              >
                {option.icon}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}

        <button className="aina-button" onClick={onButtonClick}>
          {hasMenu || options[0].id === "chatbot" ? (
            // Menu, or chatbot-only: keep the original AINA logo icon.
            <Image
              src={getImagePath("chatbot_logo.png")}
              width={40}
              height={40}
              style={{ objectFit: "contain" }}
              alt="aina"
            />
          ) : (
            // Single non-chatbot option (e.g. support-only): use its own icon.
            <span className="aina-button__single-icon">{options[0].icon}</span>
          )}
        </button>
      </div>

      <div
        className={`help-popup-backdrop ${
          isChatbotOpen || isSupportOpen ? "is-open" : ""
        }`}
        onClick={() => {
          setIsChatbotOpen(false);
          setIsSupportOpen(false);
        }}
      />

      {showChatbot && chatbotLoaded && (
        <div className={`help-popup ${isChatbotOpen ? "is-open" : ""}`}>
          <div className="help-popup__header">
            <span>AINA</span>
            <button
              className="help-popup__close"
              onClick={() => setIsChatbotOpen(false)}
              aria-label="Close chatbot"
            >
              <IoCloseSharp />
            </button>
          </div>
          <iframe
            key={authToken ?? "anon"}
            className="help-popup__frame"
            src={chatbotLink}
            title="AINA"
            allow="geolocation"
          />
          <ChatbotSplash
              loaded={chatbotFrameReady}
              resetKey={chatbotFrameKey}
              minMs={3000}
              maxMs={3000}
            />
        </div>
      )}

      {showSupport && supportLoaded && (
        <div className={`help-popup ${isSupportOpen ? "is-open" : ""}`}>
          <div className="help-popup__header">
            <span>Help &amp; Support</span>
            <button
              className="help-popup__close"
              onClick={() => setIsSupportOpen(false)}
              aria-label="Close help"
            >
              <IoCloseSharp />
            </button>
          </div>
          <iframe
            key={authToken ?? "anon"}
            className="help-popup__frame"
            src={supportLink}
            title="Help & Support"
          />
        </div>
      )}
    </>
  );
}
